import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import AddRounded from '@mui/icons-material/AddRounded';
import Typography from '@mui/material/Typography';

import ParentBatchSummary from './ParentBatchSummary';
import ShipmentWizardLayout from './ShipmentWizardLayout';
import Shipments from './Shipments';

const ShipmentsFormCard = ({
  batchOwnerName,
  dataCompanyId,
  initialShipmentState,
  initialValidationState,
  locationHelperText,
  onClose,
  onNext,
  originId,
  originName,
  productId,
  setComparisonState,
  setLocationHelperText,
  setShipmentState,
  setValidationMessages,
  shipmentState,
  totalProposedVolume,
  validationMessages,
}) => {
  const { shipments } = shipmentState;

  // Given to shipment forms for updating state of a specific shipment.
  // updateShipmentState :: i -- index of shipment to modify
  //                  -> f -- function to transform shipment
  //                  -> ()
  const updateShipmentState = R.curry((i, f) => {
    setShipmentState(s => R.assoc('shipments', R.adjust(i, f, s.shipments), s));
  });

  // Given to shipment forms for updating the location of the initial state
  // used to check for user changes to the shipment form
  // updateComparisonState :: i -- index of comparison shipment to modify
  //                  -> f -- function to transform comparison shipment
  //                  -> ()
  const updateComparisonState = R.curry((i, f) => {
    setComparisonState(s => R.assoc('shipments', R.adjust(i, f, s.shipments), s));
  });

  // Given to shipment forms for updating validation of a specific shipment.
  // updateValidationState :: i -- index of validation to modify
  //                            -> f -- function to transform validation
  //                            -> ()
  const updateValidationState = R.curry((i, f) => {
    setValidationMessages(R.adjust(i, f));
  });

  // Given to shipment forms for updating the location helper text of a specific shipment.
  // updateLocationHelperText ::  i -- index of location helper text to modify
  //                            -> message -- message to set as helper text
  //                            -> ()
  const updateLocationHelperText = R.curry((i, message) => {
    setLocationHelperText(R.update(i, message));
  });

  const handleAddShipperClick = () => {
    setShipmentState(R.assoc('shipments', R.append(initialShipmentState, shipments)));
    setLocationHelperText(R.append(''));
    setValidationMessages(R.append(initialValidationState));
  };

  const isInvalid = [
    R.map(R.compose(R.isNil, R.prop('shipper')), shipments),
    R.map(R.compose(R.isNil, R.prop('bookingOrigin')), shipments),
    R.map(R.compose(R.isNil, R.prop('location')), shipments),
    R.map(R.compose(R.isEmpty, R.prop('volume')), shipments),
    R.map(R.compose(R.not, R.isEmpty, R.prop('shipper')), validationMessages),
    R.map(R.compose(R.not, R.isEmpty, R.prop('volume')), validationMessages),
    R.map(R.compose(R.not, R.isEmpty, R.prop('comments')), validationMessages),
  ]
    .flat()
    .includes(true);

  const buttons = (
    <>
      <Button onClick={onClose} sx={{ width: 104 }} variant="outlined">
        Back
      </Button>
      <Button disabled={isInvalid} onClick={onNext} sx={{ width: 104 }}>
        Review
      </Button>
    </>
  );

  return (
    <ShipmentWizardLayout
      buttons={buttons}
      onClose={onClose}
      pageTitle="Add New Shipment"
      parentDetails={<ParentBatchSummary totalProposedVolume={totalProposedVolume} />}
    >
      <Card
        elevation={1}
        sx={{
          pb: 11,
          pt: { md: 3, xs: 2 },
          px: { md: 3, xs: 2 },
        }}
      >
        <CardHeader
          subheader="Please provide the following details for your shipment(s)."
          sx={{ padding: 0 }}
          title="Shipments"
        />
        <CardContent sx={{ padding: 0 }}>
          {shipments.map((shipment, index) => {
            const count = R.add(index, 1);
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                <Stack spacing={2} sx={{ py: { md: 3, xs: 2 } }}>
                  <Typography variant="subtitle1">Shipment {count}</Typography>
                  <Shipments
                    batchOwnerName={batchOwnerName}
                    dataCompanyId={dataCompanyId}
                    locationHelperText={locationHelperText[index]}
                    originId={originId}
                    originName={originName}
                    productId={productId}
                    setComparisonState={updateComparisonState(index)}
                    setLocationHelperText={updateLocationHelperText(index)}
                    setShipmentState={updateShipmentState(index)}
                    setValidationMessages={updateValidationState(index)}
                    shipmentState={shipment}
                    validationMessages={validationMessages[index]}
                  />
                </Stack>
                {count < shipments.length && <Divider />}
              </Fragment>
            );
          })}
        </CardContent>
        <Button
          onClick={handleAddShipperClick}
          startIcon={<AddRounded sx={{ color: 'secondary.main', mt: -0.5 }} />}
          variant="text"
        >
          Add Another Shipper
        </Button>
      </Card>
    </ShipmentWizardLayout>
  );
};

ShipmentsFormCard.propTypes = {
  batchOwnerName: PropTypes.string.isRequired,
  dataCompanyId: PropTypes.number.isRequired,
  initialShipmentState: PropTypes.shape({
    bookingOrigin: PropTypes.shape({}).isRequired,
    location: PropTypes.shape({}),
    shipper: PropTypes.shape({}),
  }).isRequired,
  initialValidationState: PropTypes.shape({}).isRequired,
  locationHelperText: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  originId: PropTypes.number.isRequired,
  originName: PropTypes.string.isRequired,
  productId: PropTypes.number.isRequired,
  setComparisonState: PropTypes.func.isRequired,
  setLocationHelperText: PropTypes.func.isRequired,
  setShipmentState: PropTypes.func.isRequired,
  setValidationMessages: PropTypes.func.isRequired,
  shipmentState: PropTypes.shape({
    shipments: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  totalProposedVolume: PropTypes.number.isRequired,
  validationMessages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default ShipmentsFormCard;
